// ============================================
// FILE: stateManagers/services/AuthContext.tsx
// ============================================
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { login as loginService, logout as logoutService, validateSession, AuthError } from './authService';
import { User } from '../../types/user';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // Only true during initial auth check
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    console.log('🔍 Checking authentication status...');
    try {
      const isValid = await validateSession();
      
      if (isValid) {
        console.log('✅ Session valid');
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
        } else {
          // If no stored user, clear everything
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        console.log('❌ Session invalid or expired');
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem('user');
      }
    } catch (err) {
      console.error('❌ Auth check failed:', err);
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('user');
    } finally {
      setLoading(false); // Only set loading false after initial check
    }
  };

  const login = async (email: string, password: string) => {
    // CRITICAL: Don't set loading to true here - it causes remount
    // The LoginScreen component will handle its own loading state
    setError(null);
    
    try {
      const response = await loginService(email, password);
      const userData = response.data.user;
      
      if (userData) {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        console.log('✅ User data stored:', userData);
      }
      
      setIsAuthenticated(true);
      console.log('✅ Login successful');
    } catch (err) {
      console.error('❌ Login error:', err);
      
      let errorMessage = 'Login failed. Please try again.';
      
      if (err instanceof AuthError) {
        errorMessage = err.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      // Set error but don't throw - let the component handle it
      setError(errorMessage);
      setIsAuthenticated(false);
      setUser(null);
      
      // Re-throw so LoginScreen can catch and display
      throw err;
    }
  };

  const logout = async () => {
    // CRITICAL: Don't set loading to true here either
    try {
      await logoutService();
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('user');
      console.log('✅ Logout successful');
    } catch (err) {
      console.error('❌ Logout error:', err);
      // Still clear local state even if API call fails
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('user');
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    loading,
    error,
    login,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
